// Per-field allowed-value controls, shown in the "Fields in this template" list
// (feature 004). A field with one or more values becomes a dropdown at fill
// time; a field with none stays free text. See contracts/ui-and-layout.md §1.
//
// Values are validated with the SAME shared rule the server uses
// (parseFieldOptions), so a value accepted here is never rejected on save. They
// are only ever shown as React text/input values — never markup — so an authored
// value cannot inject script (FR-013).

import { useState, type JSX, type KeyboardEvent } from 'react';
import { parseFieldOptions } from '@template-filler/core';

interface FieldOptionsEditorProps {
  name: string;
  values: string[];
  onChange: (values: string[]) => void;
}

export function FieldOptionsEditor({ name, values, onChange }: FieldOptionsEditorProps): JSX.Element {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | undefined>();

  const isDropdown = values.length > 0;

  // Validate the proposed set with the shared rule; on success commit the
  // trimmed, validated values so what is stored can never be rejected on save.
  function commit(candidate: string[], onFail?: () => void): boolean {
    const result = parseFieldOptions({ [name]: candidate });
    if (!result.ok) {
      setError(result.issues[0]?.message ?? 'That value cannot be used.');
      onFail?.();
      return false;
    }
    onChange(result.value[name] ?? []);
    setError(undefined);
    return true;
  }

  function addValue(): void {
    if (commit([...values, draft])) setDraft('');
  }

  function editValue(index: number, raw: string, revert: () => void): void {
    commit(
      values.map((value, i) => (i === index ? raw : value)),
      revert,
    );
  }

  function removeValue(index: number): void {
    onChange(values.filter((_, i) => i !== index));
    setError(undefined);
  }

  function clearAll(): void {
    onChange([]);
    setError(undefined);
  }

  const errorId = error ? `field-options-error-${name}` : undefined;

  return (
    <div className="field-options" role="group" aria-label={`Allowed values for “${name}”`}>
      <p className="hint">
        {isDropdown
          ? 'Filled by choosing one of these from a dropdown.'
          : 'Filled as free text. Add values below to constrain it to a fixed list.'}
      </p>

      {isDropdown ? (
        <ul className="field-options-list">
          {values.map((value, index) => (
            <li key={`${index}:${value}`}>
              <input
                aria-label={`Allowed value ${index + 1} for “${name}”`}
                defaultValue={value}
                maxLength={200}
                onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    event.currentTarget.blur();
                  }
                }}
                onBlur={(event) => {
                  const next = event.target.value;
                  if (next.trim() === value) return; // unchanged
                  editValue(index, next, () => {
                    event.target.value = value;
                  });
                }}
              />
              <button
                type="button"
                aria-label={`Remove “${value}”`}
                onClick={() => removeValue(index)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="field-options-add">
        <input
          aria-label={`Add an allowed value for “${name}”`}
          value={draft}
          maxLength={200}
          {...(errorId ? { 'aria-describedby': errorId } : {})}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(undefined);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addValue();
            }
          }}
        />
        <button type="button" onClick={addValue}>
          Add value
        </button>
        {isDropdown ? (
          <button
            type="button"
            className="btn-ghost"
            aria-label={`Remove all allowed values for “${name}”`}
            onClick={clearAll}
          >
            Clear all
          </button>
        ) : null}
      </div>

      {error ? (
        <p className="error" id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
